import { Accordion } from "@inno-ui/accordion";
import { Button, ButtonColors, ButtonVariants } from "@inno-ui/button";
import { CheckmarkIcon, ChevronLeftIcon, PlusIcon } from "@inno-ui/icons";
import { toast } from "@inno-ui/toast";
import cn from "classnames";
import { useEffect, useMemo, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router";

import { useBookEventSlot } from "../../../../hooks/api/use-book-event-slot";
import { useAppForm } from "../../../../hooks/use-form";
import { useSelectedDate } from "../../../../hooks/use-selected-date";
import { useAppointmentStore } from "../../../../store/appointment/appointment-store";
import { CONTACT_FORM_INITIAL_VALUES } from "../../../../store/appointment/appointment-store.constants";
import { useGeneralStore } from "../../../../store/general/general-store";
import { externalRedirectUrl, extractUrlFormParams } from "../../../../utils/helpers/general";
import { createAppointmentFormSchema } from "../../../desktop/screens/appointment-content/appointment-form/appointment-form.schema";
import { AppointmentFormMobile } from "../../appointment-form-mobile/appointment-form-mobile";
import MobileFooter from "../../mobile-footer/mobile-footer";
import { AddedGuestsMobile } from "./added-guests-mobile/added-guests-mobile";
import styles from "./contact.module.scss";

type ContactProps = {
  isEmbed?: boolean;
};

export function Contact({ isEmbed = false }: ContactProps) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { t } = useTranslation();
  const { onPrev, showAddGuestForm, language } = useGeneralStore();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isRejectionUrl = location.pathname.split("/")[1].toLowerCase() === "rej";
  const referrer = searchParams.get("referrer");
  const { mutateAsync: bookEventSlot, isPending: isBookingEvent } = useBookEventSlot();
  const { onNext, currentTimezone, setIsConfirmationPage } = useGeneralStore();
  const { fromFormatted, toFormatted } = useSelectedDate();
  const [isFieldsReady, setIsFieldsReady] = useState(false);

  const {
    otherParticipants,
    setContactDetails,
    currentEventTypeId,
    customFieldsKeys,
    userAppointmentData,
    initialContactFormData,
    setContactFormData,
  } = useAppointmentStore();

  const customFieldsMemoized = useMemo(
    () =>
      JSON.parse(userAppointmentData?.extra?.event_type?.payload ?? "{}")?.custom_fields ?? [],
    [userAppointmentData?.extra?.event_type?.payload],
  );

  const appointmentFormSchema = useMemo(
    () => createAppointmentFormSchema(customFieldsMemoized),
    [customFieldsMemoized],
  );

  // Extract URL parameters for auto-filling
  const urlParams = extractUrlFormParams();

  // Merge URL parameters with initial contact form data
  const mergedInitialValues = {
    ...initialContactFormData,
    ...urlParams,
  };

  const form = useAppForm({
    defaultValues: mergedInitialValues,
    onSubmit: async ({ value }) => {
      const captchaToken = await executeRecaptcha?.("book_appointment");

      const contactDetails = {
        ...value,
        id: Math.random().toString(),
        gender: value.gender.value,
        phone: value.phone,
        isBooker: true,
      };

      const otherParticipantsWithIsBooker = otherParticipants.map(participant => ({
        ...participant,
        isBooker: false,
      }));

      const payload = customFieldsKeys?.reduce(
        (acc, field, index) => ({ ...acc, [field]: value.customFields[index] }),
        {},
      );

      setContactDetails(contactDetails);
      const response = await bookEventSlot({
        event_type_id: currentEventTypeId,
        from: fromFormatted,
        to: toFormatted,
        timezone: currentTimezone,
        attendees: [contactDetails, ...otherParticipantsWithIsBooker],
        payload: JSON.stringify(payload),
        recaptcha_token: captchaToken ?? "",
        lang: language,
      });

      if (response.code === 200) {
        // add ?booked=true to the url in every case except for rejection url
        if (!isRejectionUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set("booked", "true");
          window.history.replaceState({}, "", url.toString());
        }
        // if there's a referrer, redirect to the external url
        if (referrer) {
          const redirectUrl = externalRedirectUrl(response?.data?.calendar_event_uuid, language);
          window.location.replace(redirectUrl);
          return;
        }

        form.reset();
        setIsConfirmationPage(true);
        setContactFormData(CONTACT_FORM_INITIAL_VALUES);
        onNext?.();
        return;
      }

      const error = typeof response?.error === "string" ? response.error : Object?.values(response?.error ?? {})[0];
      toast.error({ title: error as string });
    },
    validators: {
      onSubmit: appointmentFormSchema,
    },
  });

  const addGuestHandler = () => {
    showAddGuestForm();
    const formValues = {
      firstName: form.getFieldValue("firstName"),
      lastName: form.getFieldValue("lastName"),
      email: form.getFieldValue("email"),
      phone: form.getFieldValue("phone"),
      gender: form.getFieldValue("gender"),
      message: form.getFieldValue("message"),
      customFields: form.getFieldValue("customFields") || [],
    };
    setContactFormData(formValues);
  };

  useEffect(() => {
    const hasCustomFields = customFieldsMemoized?.custom_fields?.length > 0;
    setIsFieldsReady(!hasCustomFields || true);
  }, [userAppointmentData?.extra?.event_type?.payload]);

  // Auto-fill form fields from URL parameters on component mount
  useEffect(() => {
    const urlParams = extractUrlFormParams();
    if (urlParams.firstName)
      form.setFieldValue("firstName", urlParams.firstName);
    if (urlParams.lastName)
      form.setFieldValue("lastName", urlParams.lastName);
    if (urlParams.email)
      form.setFieldValue("email", urlParams.email);
    if (urlParams.phone)
      form.setFieldValue("phone", urlParams.phone);
    if (urlParams.gender.value)
      form.setFieldValue("gender", urlParams.gender);
    if (urlParams.message)
      form.setFieldValue("message", urlParams.message);
  }, [form]);

  return (
    <div className={cn(styles.appointmentWrapper, !isEmbed && styles.appointmentWrapperScrollable)}>
      <div className={styles.appointment}>
        <Accordion title={t("contact")} noPaddings noBackground defaultExpanded={isFieldsReady}>
          <AppointmentFormMobile form={form} />
        </Accordion>
        {!!otherParticipants.length && <AddedGuestsMobile />}
        <Button
          variant={ButtonVariants.Light}
          label={t("add-guest")}
          width="100%"
          icon={PlusIcon}
          onClick={addGuestHandler}
        />
      </div>
      <div className={styles.contactBottom}>
        <div>
          <form.AppField name="message">
            {field => <field.TextAreaField placeholder={t("please-enter")} label={t("message")} />}
          </form.AppField>

        </div>
        <MobileFooter
          primaryButton={(
            <Button
              variant={ButtonVariants.Dark}
              label={t("book-appointment")}
              width="100%"
              onClick={() => {
                form.handleSubmit();
              }}
              isLoading={isBookingEvent}
              icon={CheckmarkIcon}
              color={ButtonColors.Green}
            />
          )}
          secondaryButton={(
            <Button
              variant={ButtonVariants.Light}
              label={t("back")}
              width="100%"
              icon={ChevronLeftIcon}
              onClick={onPrev}
            />
          )}
        />
      </div>
    </div>
  );
}
